import { DownloadOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Card, Flex, Typography, theme } from 'antd';
import { useFlags } from 'launchdarkly-react-client-sdk';
import LoaderEcho from '@/components/LoaderEcho';
import ModalEcho from '@/components/modals/Modal/ModalEcho';
import NoDataMessageEcho from '@/components/states/NoDataMessageEcho';
import { useDerivedNetworkContext } from '@/context';
import { useAgePlaysQuery } from '@/services/revenueApi';
import type { AgeData, DateRange, DownloadConfig, ShowFilter } from '@/types';
import { uppercaseFirstLetter } from '@/utils';
import {
  trackAudienceAgeModalDownloadClicked,
  trackAudienceAgeModalOpened,
} from './audienceModalMixpanel';
import useAgeCsvExport from './useAgeCsvExport';
import DataAvailabilityBanner from './DataAvailabilityBanner';
import useIsBeforeDataAvailability from './useIsBeforeDataAvailability';

const getPercentageColorIndex = (percentage: number, globalMax: number): number | null => {
  const totalBuckets = Math.ceil(globalMax / 10);
  const topBucketIndex = totalBuckets - 1;
  const bucketIndex = Math.min(Math.floor(percentage / 10), topBucketIndex);
  const bucketsFromTop = topBucketIndex - bucketIndex;

  return bucketsFromTop < 6 ? 5 - bucketsFromTop : null;
};

const ColorBandLegend = ({
  globalMax,
  colors,
}: {
  globalMax: number;
  colors: readonly string[];
}) => {
  const { token } = theme.useToken();
  const totalBuckets = Math.ceil(globalMax / 10);
  const topBucketIndex = totalBuckets - 1;
  const coloredBucketCount = Math.min(6, totalBuckets);

  const buckets = Array.from({ length: coloredBucketCount }, (_, colorRank) => {
    const bucketIndex = topBucketIndex - colorRank;
    const lower = bucketIndex * 10;
    const upper = colorRank === 0 ? globalMax : (bucketIndex + 1) * 10;
    return { color: colors[5 - colorRank], lower, upper };
  }).reverse();

  return (
    <Flex justify="center" wrap gap={token.margin}>
      {buckets.map(({ color, lower, upper }) => (
        <Flex key={lower} align="center" gap={token.marginXXS}>
          <span
            aria-hidden
            style={{
              width: token.fontSizeSM,
              height: token.fontSizeSM,
              flexShrink: 0,
              borderRadius: token.borderRadiusSM,
              background: color,
              border: `1px solid ${token.colorBorderSecondary}`,
            }}
          />
          <Typography.Text style={{ color: token.colorTextSecondary, fontSize: token.fontSizeSM }}>
            {lower}–{upper}%
          </Typography.Text>
        </Flex>
      ))}
    </Flex>
  );
};

const AgePlatformCard = ({
  platform,
  ageDistribution,
  medianAge,
  globalMax,
  colors,
}: {
  platform: string;
  ageDistribution: AgeData[];
  medianAge: number;
  globalMax: number;
  colors: readonly string[];
}) => {
  const { token } = theme.useToken();

  return (
    <Card
      size="small"
      title={
        <Flex vertical>
          <Typography.Text strong>
            {uppercaseFirstLetter(platform.replace(/_/g, ' '))}
          </Typography.Text>
          <Typography.Text style={{ color: token.colorTextSecondary, fontSize: token.fontSizeSM }}>
            Median age: {medianAge}
          </Typography.Text>
        </Flex>
      }
      style={{ flex: '1 1 220px', minWidth: 220, maxWidth: '25%', overflow: 'hidden' }}
      styles={{ body: { padding: 0 } }}
    >
      <Flex vertical>
        {ageDistribution.map(({ label, percentage }) => {
          const colorIndex = getPercentageColorIndex(percentage, globalMax);

          return (
            <Flex
              key={label}
              justify="space-between"
              gap={token.marginXS}
              style={{
                padding: `${token.paddingXS}px ${token.padding}px`,
                background: colorIndex === null ? undefined : colors[colorIndex],
                borderTop: `1px solid ${token.colorBorderSecondary}`,
              }}
            >
              <Typography.Text style={{ fontSize: token.fontSizeSM }}>{label}</Typography.Text>
              <Typography.Text style={{ fontSize: token.fontSizeSM }}>
                {percentage}%
              </Typography.Text>
            </Flex>
          );
        })}
      </Flex>
    </Card>
  );
};

const AgeModalContent = ({
  dateRange,
  selectedShow,
}: {
  dateRange: DateRange;
  selectedShow: ShowFilter;
}) => {
  const { token } = theme.useToken();
  const { selectedNetwork } = useDerivedNetworkContext();
  const { audienceSources } = useFlags<{ audienceSources?: string[] }>();
  const sources = audienceSources ?? ['acast', 'phyllo'];
  const showIds = selectedShow !== 'all' && selectedShow ? [selectedShow] : undefined;

  const { showBanner, hideContent, queryFrom } = useIsBeforeDataAvailability(
    'age',
    dateRange.from,
    dateRange.to
  );

  const { data, isPending } = useAgePlaysQuery(
    selectedNetwork.id,
    queryFrom,
    dateRange.to,
    hideContent ? { enabled: false } : undefined,
    { showIds, sources }
  );

  if (hideContent) {
    return <DataAvailabilityBanner dataType="age" />;
  }

  if (isPending) {
    return (
      <Flex justify="center" align="center" style={{ height: '60vh' }}>
        <LoaderEcho data-testid="loader" />
      </Flex>
    );
  }

  if (!data || data.plays.length < 1) {
    return (
      <>
        {showBanner && <DataAvailabilityBanner dataType="age" />}
        <NoDataMessageEcho dataType="age data" />
      </>
    );
  }

  const globalMax = Math.max(
    ...data.plays.flatMap(({ age_distribution }) =>
      age_distribution.map(({ percentage }) => percentage)
    ),
    1
  );
  const colors = [
    token.colorBgContainer,
    token.colorPrimaryBg,
    token.colorPrimaryBgHover,
    token.colorPrimaryBorder,
    token.colorPrimaryBorderHover,
    token.colorPrimary,
  ] as const;

  return (
    <Flex vertical gap={token.marginLG} style={{ padding: token.padding }}>
      {showBanner && <DataAvailabilityBanner dataType="age" />}
      <ColorBandLegend globalMax={globalMax} colors={colors} />
      <Flex justify="center" wrap gap={token.marginLG}>
        {data.plays.map(({ platform, age_distribution, median_age }) => (
          <AgePlatformCard
            key={platform}
            platform={platform}
            ageDistribution={age_distribution}
            medianAge={median_age}
            globalMax={globalMax}
            colors={colors}
          />
        ))}
      </Flex>
    </Flex>
  );
};

const AgeModalEcho = () => {
  const { handleCSVExport } = useAgeCsvExport();

  return (
    <ModalEcho
      hashProp="age-details"
      title="Age details"
      showDateRangeFilter
      onOpen={trackAudienceAgeModalOpened}
      downloadConfig={{
        onDownload: (...args) => {
          trackAudienceAgeModalDownloadClicked();
          return handleCSVExport(...args);
        },
        downloadButtonText: 'Export as CSV',
        downloadButtonIcon: DownloadOutlined as DownloadConfig['downloadButtonIcon'],
      }}
      renderTrigger={(openModal) => (
        <Flex justify="flex-end" style={{ alignSelf: 'flex-end' }}>
          <Button
            type="text"
            color="primary"
            variant="text"
            icon={<RightOutlined />}
            iconPlacement="end"
            onClick={openModal}
          >
            More details
          </Button>
        </Flex>
      )}
    >
      {(dateRange, selectedShow) => (
        <AgeModalContent dateRange={dateRange} selectedShow={selectedShow} />
      )}
    </ModalEcho>
  );
};

export default AgeModalEcho;
